import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import { LockOutlined as LockIcon } from '@mui/icons-material';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const CheckoutForm = ({ clientSecret, amount, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (error) {
        setError(error.message);
        console.error("Payment error:", error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        setSuccess(true);
        if (onSuccess) {
          onSuccess(paymentIntent);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Unexpected payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          textAlign: 'center',
          bgcolor: alpha(theme.palette.success.main, 0.1),
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: theme.palette.success.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              mb: 2,
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
            </svg>
          </Box>
          
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Payment Successful!
          </Typography>
          
          <Typography variant="body1" color="text.secondary">
            Your payment has been processed successfully. Thank you for your subscription!
          </Typography>
          
          <Button 
            variant="contained" 
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => window.location.reload()}
          >
            Continue
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Card Information
        </Typography>
        
        <Box 
          sx={{ 
            p: 2, 
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            '&:focus-within': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
      
      <Divider sx={{ my: 3 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LockIcon fontSize="small" sx={{ color: theme.palette.success.main, mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Secure payment via Stripe
          </Typography>
        </Box>
        
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!stripe || loading}
          sx={{ 
            py: 1.5, 
            px: 4,
            fontWeight: 600,
            position: 'relative',
          }}
        >
          {loading ? (
            <>
              <CircularProgress 
                size={24} 
                sx={{ 
                  color: 'white',
                  position: 'absolute',
                  left: '50%',
                  marginLeft: '-12px',
                }} 
              />
              <Typography sx={{ visibility: 'hidden' }}>Pay Now</Typography>
            </>
          ) : (
            `Pay ${amount ? amount : 'Now'}`
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CheckoutForm;
